import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function VoicePipelineSection() {
  const { voiceLatencySteps, callerIdFeatures } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); setIsRunning(true); } },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isRunning) return;
    const timer = setInterval(() => {
      setActiveStep((p) => (p + 1) % voiceLatencySteps.length);
    }, 1100);
    return () => clearInterval(timer);
  }, [isRunning, voiceLatencySteps.length]);

  const pipelineFeatures = [
    { icon: "ri-exchange-line", title: t("voice_ws_title"), desc: t("voice_ws_desc") },
    { icon: "ri-sound-module-line", title: t("voice_a2a_title"), desc: t("voice_a2a_desc") },
    { icon: "ri-apps-2-line", title: t("voice_agnostic_title"), desc: t("voice_agnostic_desc") },
  ];

  return (
    <section
      ref={ref}
      className="py-28"
      style={{ background: "linear-gradient(180deg, #0C0C0E 0%, #111113 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-400 mb-4">
            {t("voice_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("voice_title_1")}
            <br />
            <span className="text-amber-400">{t("voice_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-400 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("voice_subtitle")}
          </p>
        </div>

        <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative bg-white/4 border border-white/10 rounded-3xl p-8 lg:p-10 mb-12">
            <div className="flex items-center justify-between mb-8">
              <p className="text-white text-sm font-semibold">{t("voice_pipeline_title")}</p>
              <button
                onClick={() => setIsRunning(!isRunning)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  isRunning
                    ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-300"
                    : "bg-white/10 border border-white/20 text-stone-400"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? "bg-emerald-400 animate-pulse" : "bg-stone-500"}`} />
                {isRunning ? t("voice_simulating") : t("voice_start_sim")}
              </button>
            </div>

            <div className="relative">
              <div className="absolute top-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <div className="grid grid-cols-5 gap-3">
                {voiceLatencySteps.map((step, i) => {
                  const isPast = i < activeStep;
                  const isCurrent = i === activeStep;
                  return (
                    <div key={step.step} className="flex flex-col items-center text-center">
                      <div
                        className={`relative w-12 h-12 flex items-center justify-center rounded-full border-2 transition-all duration-500 mb-4 z-10 ${
                          isCurrent ? "border-amber-400 bg-amber-400/20 scale-110" : isPast ? "border-emerald-500 bg-emerald-500/20" : "border-white/15 bg-white/5"
                        }`}
                      >
                        {isPast ? (
                          <i className="ri-check-line text-emerald-400 text-lg" />
                        ) : (
                          <i className={`${step.icon} text-lg ${isCurrent ? "text-amber-400" : "text-stone-600"}`} />
                        )}
                        {isCurrent && (
                          <span className="absolute inset-0 rounded-full border-2 border-amber-400 animate-ping opacity-40" />
                        )}
                      </div>
                      <p className={`text-xs font-bold mb-1 transition-colors duration-300 ${isCurrent ? "text-amber-400" : isPast ? "text-emerald-400" : "text-stone-500"}`}>
                        {step.label}
                      </p>
                      <p className="text-stone-600 text-xs leading-tight mb-2 hidden lg:block">{step.desc}</p>
                      <span
                        className={`text-xs font-black px-2 py-0.5 rounded-full transition-all duration-300 ${
                          isCurrent ? "bg-amber-400/20 text-amber-300 border border-amber-400/30" : isPast ? "bg-emerald-500/15 text-emerald-400" : "text-stone-600"
                        }`}
                        dangerouslySetInnerHTML={{ __html: step.ms }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/8 grid sm:grid-cols-3 gap-4">
              {pipelineFeatures.map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 bg-white/4 rounded-xl border border-white/8">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-400/15 flex-shrink-0">
                    <i className={`${item.icon} text-amber-400 text-base`} />
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold mb-1">{item.title}</p>
                    <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-white/4 border border-white/10 rounded-3xl p-8 lg:p-10">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-400/15 border border-amber-400/20 flex-shrink-0">
                <i className="ri-phone-find-line text-amber-400 text-2xl" />
              </div>
              <div>
                <h3 className="text-white text-xl font-bold mb-1">{t("voice_delivery_title")}</h3>
                <p className="text-stone-400 text-sm leading-relaxed">{t("voice_delivery_desc")}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              {callerIdFeatures.map((feat, i) => (
                <div
                  key={feat.title}
                  className={`p-6 bg-white/4 border border-white/8 rounded-2xl hover:bg-white/8 hover:-translate-y-1 transition-all duration-300 cursor-default ${visible ? "opacity-100" : "opacity-0"}`}
                  style={{ transitionDelay: `${300 + i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/15 mb-4">
                    <i className={`${feat.icon} text-amber-400 text-xl`} />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-2">{feat.title}</h4>
                  <p className="text-stone-400 text-xs leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-center gap-3 p-4 bg-amber-400/8 border border-amber-400/20 rounded-2xl">
              <i className="ri-arrow-up-line text-amber-400 text-xl" />
              <p className="text-amber-300 text-sm">{t("voice_answer_rate")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
